// ---------------------------------------------------------------------------
// LA TV LIT, LE TÉLÉPHONE PILOTE (Google Cast, Chromecast seulement).
//
// Toji (23/10) : « lecteur compatible Chromecast depuis le téléphone ; le
// téléphone pilote : lecture, pause, avance, épisode suivant. La TV lit. »
//
// Récepteur PAR DÉFAUT de Google (aucune appli à publier) : il lit du MP4.
// Le serveur lui sert donc toujours le cran `remux` (MP4 fragmenté) par une
// adresse à jeton court (`POST /api/cast/:ref`) — la TV n'a ni cookie ni
// session. Ce flux ne se déplace pas : un saut = une nouvelle adresse à
// `pos=`, et la seconde réelle = `depart + currentTime` de la TV.
//
// shortcut: pas de sous-titres sur la TV (le récepteur par défaut exige des
// WebVTT servis en CORS) — à ajouter avec un récepteur personnalisé si besoin.
// ---------------------------------------------------------------------------

const SDK = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';

/** La seconde réelle à la TV, bornée à [0, durée]. PURE. */
export function secondeSurLaTv(depart, courant, duree = 0) {
  const s = Math.max(0, (Number(depart) || 0) + (Number(courant) || 0));
  return duree > 0 ? Math.min(s, duree) : s;
}

let promesseSdk = null;
function chargerSdk() {
  if (promesseSdk) return promesseSdk;
  promesseSdk = new Promise((resoudre) => {
    // Pas de Cast dans l'activité Discord (iframe) ni hors de Chrome : le SDK
    // ne s'y annonce jamais, on n'attend pas pour rien.
    if (window.self !== window.top || !window.chrome) return resoudre(false);
    window.__onGCastApiAvailable = (dispo) => resoudre(!!dispo);
    const s = document.createElement('script');
    s.src = SDK;
    s.async = true;
    s.onerror = () => resoudre(false);
    document.head.append(s);
    setTimeout(() => resoudre(false), 15_000);
  });
  return promesseSdk;
}

/**
 * @param {{
 *   emplacement: HTMLElement,   // où poser le bouton Cast (barre du lecteur)
 *   racine: HTMLElement,        // le lecteur, qui reçoit le panneau « sur la TV »
 *   courant: () => ({ ref, seconde, audio, titre, sousTitre, image, duree, serie }|null),
 *   pauseLocale: () => void,
 *   reprendreLocale: (seconde) => void,
 *   suivant: () => void,
 *   precedent: () => void,
 *   jeton?: () => string|null,
 * }} o
 */
export function brancherCast(o) {
  const etat = { actif: false, depart: 0, ref: null, nom: '' };
  let ctx = null;
  let joueur = null;
  let manette = null;
  let panneau = null;

  const seconde = () => secondeSurLaTv(etat.depart, joueur?.currentTime, o.courant()?.duree);

  async function envoyer(depuis) {
    const c = o.courant();
    const session = ctx?.getCurrentSession();
    if (!c?.ref || !session) return;
    const pos = Math.max(0, Math.floor(depuis ?? c.seconde ?? 0));
    const entetes = { 'content-type': 'application/json' };
    const j = o.jeton?.();
    if (j) entetes.authorization = `Bearer ${j}`;
    const r = await fetch(`/api/cast/${encodeURIComponent(c.ref)}`, {
      method: 'POST', credentials: 'same-origin', headers: entetes, body: JSON.stringify({ pos, audio: c.audio ?? 0 }),
    });
    if (!r.ok) { dire(`La TV n’a pas pu recevoir la vidéo (${r.status}).`); return; }
    const { url, type } = await r.json();
    const media = new chrome.cast.media.MediaInfo(url, type || 'video/mp4');
    media.streamType = chrome.cast.media.StreamType.BUFFERED;
    if (c.duree > 0) media.duration = Math.max(1, c.duree - pos);
    const meta = c.serie ? new chrome.cast.media.TvShowMediaMetadata() : new chrome.cast.media.MovieMediaMetadata();
    meta.title = c.titre || 'Tsudoi';
    if (c.sousTitre) meta.subtitle = c.sousTitre;
    if (c.image) meta.images = [new chrome.cast.Image(new URL(c.image, location.href).href)];
    media.metadata = meta;
    const demande = new chrome.cast.media.LoadRequest(media);
    demande.autoplay = true;
    etat.depart = pos;
    etat.ref = c.ref;
    o.pauseLocale();
    dire('Envoi à la TV…');
    try { await session.loadMedia(demande); dire(''); } catch (e) { dire(`La TV a refusé la vidéo (${e?.code ?? e}).`); }
  }

  function dire(texte) {
    const t = panneau?.querySelector('.cast-etat');
    if (t) t.textContent = texte;
  }

  function montrerPanneau(oui) {
    if (!oui) { panneau?.remove(); panneau = null; return; }
    if (panneau) return;
    panneau = document.createElement('div');
    panneau.className = 'cast-panneau';
    panneau.setAttribute('role', 'region');
    panneau.setAttribute('aria-label', 'Lecture sur la TV');
    panneau.innerHTML = `
      <p class="cast-titre">Lecture sur <b></b></p>
      <p class="cast-temps" aria-live="off"></p>
      <div class="cast-boutons">
        <button type="button" data-a="prec" aria-label="Épisode précédent">⏮</button>
        <button type="button" data-a="recul" aria-label="Reculer de 10 secondes">−10</button>
        <button type="button" data-a="pause" class="cast-principal" aria-label="Lecture ou pause">⏯</button>
        <button type="button" data-a="avance" aria-label="Avancer de 30 secondes">+30</button>
        <button type="button" data-a="suiv" aria-label="Épisode suivant">⏭</button>
      </div>
      <p class="cast-etat" aria-live="polite"></p>
      <button type="button" data-a="stop" class="cast-stop">Arrêter sur la TV et reprendre ici</button>`;
    panneau.querySelector('b').textContent = etat.nom || 'la TV';
    panneau.addEventListener('click', (ev) => {
      const a = ev.target.closest('button')?.dataset.a;
      if (a === 'pause') manette?.playOrPause();
      else if (a === 'recul') envoyer(seconde() - 10);
      else if (a === 'avance') envoyer(seconde() + 30);
      else if (a === 'suiv') o.suivant();
      else if (a === 'prec') o.precedent();
      else if (a === 'stop') ctx?.endCurrentSession(true);
    });
    o.racine.append(panneau);
  }

  function rafraichirTemps() {
    const t = panneau?.querySelector('.cast-temps');
    if (!t) return;
    const s = Math.floor(seconde());
    const f = (x) => `${Math.floor(x / 60)}:${String(Math.floor(x % 60)).padStart(2, '0')}`;
    const d = o.courant()?.duree;
    t.textContent = d > 0 ? `${f(s)} / ${f(d)}` : f(s);
  }

  chargerSdk().then((dispo) => {
    if (!dispo || !window.cast?.framework) return;
    ctx = cast.framework.CastContext.getInstance();
    ctx.setOptions({
      receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
    });
    joueur = new cast.framework.RemotePlayer();
    manette = new cast.framework.RemotePlayerController(joueur);
    const bouton = document.createElement('google-cast-launcher');
    bouton.className = 'l-ic l-cast';
    bouton.setAttribute('aria-label', 'Lire sur la TV');
    bouton.title = 'Lire sur la TV (Chromecast)';
    o.emplacement.append(bouton);
    manette.addEventListener(cast.framework.RemotePlayerEventType.CURRENT_TIME_CHANGED, rafraichirTemps);
    ctx.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, (ev) => {
      const S = cast.framework.SessionState;
      if (ev.sessionState === S.SESSION_STARTED || ev.sessionState === S.SESSION_RESUMED) {
        etat.actif = true;
        etat.nom = ev.session.getCastDevice()?.friendlyName ?? '';
        montrerPanneau(true);
        // Une session reprise qui lit déjà notre vidéo : on ne la recharge pas.
        if (ev.sessionState === S.SESSION_STARTED || !joueur.isMediaLoaded) envoyer();
      } else if (ev.sessionState === S.SESSION_ENDED) {
        const ou = seconde();
        const avait = etat.actif;
        etat.actif = false;
        montrerPanneau(false);
        if (avait && ou > 0) o.reprendreLocale(ou);
      }
    });
  });

  return {
    get actif() { return etat.actif; },
    /** Un autre épisode s'est ouvert pendant qu'on diffuse : il part sur la TV. */
    nouvelleSource() {
      if (!etat.actif) return;
      const c = o.courant();
      if (c?.ref && c.ref !== etat.ref) envoyer(c.seconde ?? 0);
    },
  };
}
